import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import InteractiveGridPattern from '../components/magicui/InteractiveGridPattern'
import { motion, useScroll, useTransform } from 'framer-motion'
import RichText from '../components/RichText'
import GlobalCursor from '../components/GlobalCursor'
import { aboutAPI } from '../lib/api.js'

export default function AboutPage() {
  const [aboutData, setAboutData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch about data from API
  const getAboutData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get data from API
      const response = await aboutAPI.get()
      
      if (response.data && response.data.length > 0) {
        setAboutData(response.data[0])
      } else {
        // No data available - show empty state
        setAboutData({
          title: 'A Glimpse About Me',
          subtitle: 'Professional background & expertise',
          content: {
            text: 'Content will be available soon.',
          },
        })
      }
    } catch (error) {
      console.error('Error loading about data:', error)
      setError('Failed to load about content')
      // Set minimal fallback data on error
      setAboutData({
        title: 'A Glimpse About Me',
        subtitle: 'Professional background & expertise',
        content: {
          text: 'Unable to load content at this time.',
        },
      })
    } finally {
      setLoading(false)
    }
  }

  // Listen for localStorage changes to refresh about data
  useEffect(() => {
    // Initial fetch on mount (same pattern as Now page)
    getAboutData()

    const handleStorageChange = (e) => {
      if (e.key === 'helloheman_about' || e.type === 'aboutDataUpdated') {
        getAboutData()
      }
    }

    const handleCustomEvent = (e) => {
      if (e.detail?.key === 'helloheman_about') {
        getAboutData()
      }
    }

    // Listen for localStorage changes (cross-tab)
    window.addEventListener('storage', handleStorageChange)
    
    // Listen for custom events (same-tab)
    window.addEventListener('aboutDataUpdated', handleCustomEvent)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('aboutDataUpdated', handleCustomEvent)
    }
  }, [])


  // Scroll animation setup
  const { scrollYProgress } = useScroll()
  const leftTextX = useTransform(scrollYProgress, [0, 0.5], [0, -200])
  const rightTextX = useTransform(scrollYProgress, [0, 0.5], [0, 200])
  const taglineOpacity = useTransform(scrollYProgress, [0, 0.25, 0.3], [1, 1, 0])
  const mobileDesignationChange = useTransform(scrollYProgress, [0, 0.01], [0, 1])


  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      <GlobalCursor />
      
      {/* Fixed About Image Background */}
      <div className="fixed inset-0 z-0">
        <img
          src="/About-image-glass-effect.jpg"
          alt="About Header Background"
          className="w-full h-full object-cover pointer-events-none"
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-40 pointer-events-none"></div>

        {/* Animated Stars Background */}
        <div className="absolute inset-0 stars"></div>
        <div className="absolute inset-0 stars2"></div>
      </div>

      {/* Interactive Grid Pattern - Separate Layer */}
      <div className="fixed inset-0 z-51 w-full h-full pointer-events-auto">
        <InteractiveGridPattern
          className="h-full w-full"
          width={60}
          height={60}
          squares={[32, 32]}
          squaresClassName="stroke-gray-800/20 hover:fill-gray-800/10"
        />
      </div>

        {/* Header Text Over Image - Reference UI Pattern */}
        <div className="absolute inset-0 flex items-center justify-center z-[100] pointer-events-none translate-y-[-20%] md:translate-y-[-5%]">
          <div className="text-center px-4">
            <motion.h1 className="text-4xl sm:text-4xl md:text-5xl lg:text-[60px] font-normal text-white mb-1 tracking-tight flex flex-col sm:flex-row justify-center font-monument-extended">
              <motion.span style={{ x: leftTextX }} className="mr-0 sm:mr-8 mb-2 sm:mb-0">
                Heman Shakthi
              </motion.span>
              <motion.span style={{ x: rightTextX }}>Mohan Udaiyar</motion.span>
            </motion.h1>
            <motion.div
              className="text-[#E06100] text-xl md:text-xl font-medium uppercase tracking-widest group relative pointer-events-auto font-syne"
              style={{ opacity: taglineOpacity }}
            >
              {/* Desktop: Hover effect */}
              <span className="hidden md:block transition-opacity duration-300 group-hover:opacity-0">
                Multidisciplinary
              </span>
              <span className="hidden md:block absolute inset-0 text-[#E06100] text-xl font-medium uppercase tracking-widest opacity-0 transition-opacity duration-300 group-hover:opacity-100 font-syne">
                <span
                  className="inline-block group-hover:animate-fadeInUp"
                  style={{ animationDelay: '0ms' }}
                >
                  Developer.
                </span>{' '}
                <span
                  className="inline-block group-hover:animate-fadeInUp"
                  style={{ animationDelay: '150ms' }}
                >
                  Designer.
                </span>{' '}
                <span
                  className="inline-block group-hover:animate-fadeInUp"
                  style={{ animationDelay: '300ms' }}
                >
                  Editor.
                </span>{' '}
                <span
                  className="inline-block group-hover:animate-fadeInUp"
                  style={{ animationDelay: '450ms' }}
                >
                  Creator
                </span>
              </span>

              {/* Mobile: Scroll-triggered change */}
              <motion.span
                className="md:hidden font-syne text-xl"
                style={{
                  opacity: useTransform(mobileDesignationChange, (value) => (value === 1 ? 0 : 1)),
                }}
              >
                Multidisciplinary
              </motion.span>
              <motion.span
                className="md:hidden absolute inset-0 text-[#E06100] text-xl font-medium uppercase tracking-widest font-syne"
                style={{ opacity: mobileDesignationChange }}
              >
                <span className="inline-block" style={{ animationDelay: '0ms' }}>
                  Developer.
                </span>{' '}
                <span className="inline-block" style={{ animationDelay: '150ms' }}>
                  Designer.
                </span>{' '}
                <span className="inline-block" style={{ animationDelay: '300ms' }}>
                  Editor.
                </span>{' '}
                <span className="inline-block" style={{ animationDelay: '450ms' }}>
                  Creator
                </span>
              </motion.span>
            </motion.div>
          </div>
        </div>

      {/* Scrollable Content */}
      <div className="relative z-10 min-h-screen pointer-events-none">
        {/* Spacer for image height */}
        <div className="h-screen pointer-events-none"></div>

        {/* Main Content */}
        <div className="px-4 sm:px-6 py-6 sm:py-8 max-w-6xl mx-auto pointer-events-auto">
          {/* Black Container for Content */}
          <div className="bg-black border border-gray-800 rounded-2xl p-6 sm:p-8 md:p-12 shadow-2xl">
            {/* Page Header */}
              <div className="mb-12 text-center">
              <div className="text-gray-400 text-sm mb-2">{'// about'}</div>
              <motion.h1
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 font-monument-extended"
                style={{
                  fontFamily: "'Clash Display', 'Arial Black', sans-serif",
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {aboutData?.title || 'A Glimpse About Me'}
              </motion.h1>
              <motion.div
                className="text-[#E06100] text-sm font-syne uppercase font-semibold tracking-wide"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {aboutData?.subtitle || 'Professional background & expertise'}
              </motion.div>
            </div>

            <div className="text-center space-y-4">
              {/* Main Content */}
              <div className="space-y-3">
                {error ? (
                  <div className="text-center py-8">
                    <div className="text-red-400">Error loading content: {error}</div>
                  </div>
                ) : aboutData?.content ? (
                  <div className="text-white max-w-4xl mx-auto">
                    <div className="prose prose-invert prose-lg max-w-none
                                    prose-p:text-gray-300 prose-p:leading-snug prose-p:text-base sm:prose-p:text-lg prose-p:mb-2
                                    prose-strong:text-white prose-strong:font-semibold
                                    prose-headings:text-white prose-headings:font-bold
                                    text-left sm:text-center">
                      <RichText
                        data={aboutData.content}
                        enableProse={true}
                        enableGutter={false}
                        className="leading-snug"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-white leading-relaxed space-y-6 max-w-4xl mx-auto">
                    <p className="text-gray-400 text-center py-8">
                      Content will be available soon.
                    </p>
                  </div>
                )}
              </div>

              {/* Social Media Buttons */}
              <div className="flex justify-center items-center space-x-4 mt-12 pt-8 border-t border-gray-800">
                {/* Instagram Link */}
                <a
                  href="https://instagram.com/hemanshakthi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center px-4 py-2 bg-black border border-pink-400 rounded-full hover:bg-pink-500 hover:border-pink-400 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </div>
                  <span className="text-pink-400 font-medium group-hover:text-white transition-colors duration-300 underline decoration-pink-400 group-hover:decoration-white">
                    Instagram
                  </span>
                </a>

                {/* LinkedIn Link */}
                <a
                  href="https://linkedin.com/in/hemanshakthi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center px-4 py-2 bg-black border border-blue-400 rounded-full hover:bg-blue-600 hover:border-blue-400 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </div>
                  <span className="text-blue-400 font-medium group-hover:text-white transition-colors duration-300 underline decoration-blue-400 group-hover:decoration-white">
                    LinkedIn
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}